import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { submitContact, recordPageVisit } from "@/lib/api";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin,
  Send,
  Loader2,
  Heart,
  Clock
} from "lucide-react";

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    recordPageVisit("contact").catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContact(formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="hero-gradient section-padding pb-16">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="caption mb-4">{t('contact.caption', 'Get in Touch')}</p>
            <h1 className="heading-1 text-foreground mb-6">
              {t('contact.title', "We're Here to Help")}
            </h1>
            <p className="body-lg text-muted-foreground">
              {t('contact.description', "Have questions about hospice care, need support, or want to learn more about our services? Reach out to us anytime. We're here for you.")}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding pt-8 bg-muted/30" data-testid="contact-form-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="shadow-soft" data-testid="contact-info-card">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">{t('contact.phone', 'Phone')}</h3>
                      <p className="text-muted-foreground">703-578-7108</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">{t('contact.available247', 'Available 24/7')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-secondary-foreground" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">{t('contact.email', 'Email')}</h3>
                      <p className="text-muted-foreground">hospice@goodwinliving.org</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">{t('contact.responseTime', 'We respond within 24 hours')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-accent-foreground" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">{t('contact.hours', 'Hours')}</h3>
                      <p className="text-muted-foreground">{t('contact.24hours', '24 hours a day')}</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">{t('contact.7days', '7 days a week')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compassionate Note */}
              <Card className="shadow-soft bg-primary/5 border-primary/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm text-foreground leading-relaxed">
                      {t('contact.compassionateNote', "We understand that reaching out during this time can be difficult. Please know that our team is here with compassion and without judgment. Your questions and concerns matter to us.")}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-soft" data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl">{t('contact.sendMessage', 'Send Us a Message')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12 animate-fade-in" data-testid="contact-success">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Heart className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="font-serif text-xl text-foreground mb-2">{t('contact.thankYou', 'Thank You')}</h3>
                      <p className="text-muted-foreground mb-6">
                        {t('contact.messageReceived', "Your message has been received. We'll be in touch with you soon.")}
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => setSubmitted(false)}
                        data-testid="send-another-btn"
                      >
                        {t('contact.sendAnother', 'Send Another Message')}
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('contact.fullName', 'Full Name')} *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('contact.namePlaceholder', 'Your name')}
                            required
                            className="h-12"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">{t('contact.emailAddress', 'Email Address')} *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('contact.emailPlaceholder', 'your@email.com')}
                            required
                            className="h-12"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">{t('contact.phoneNumber', 'Phone Number')}</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                            className="h-12"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">{t('contact.subject', 'Subject')} *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder={t('contact.subjectPlaceholder', 'How can we help?')}
                            required
                            className="h-12"
                            data-testid="contact-subject-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">{t('contact.message', 'Message')} *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder={t('contact.messagePlaceholder', 'Tell us how we can help you...')}
                          required
                          rows={6}
                          className="resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="btn-primary w-full md:w-auto"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {t('contact.sending', 'Sending...')}
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            {t('contact.sendMessageBtn', 'Send Message')}
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
